import { useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Turnstile } from "@/components/Turnstile";
import { authLogin, authResendVerification, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

const field = "w-full border-2 border-zinc-300 focus:border-[#2F6DA3] focus:outline-none px-4 py-3 text-sm transition-colors";
const label = "block text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-1.5";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [token, setToken] = useState("");
  const onVerify = useCallback((t) => setToken(t), []);
  const widget = useRef(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { setError("Please complete the anti-bot check below."); return; }
    setLoading(true); setError(""); setNeedsVerify(false);
    try {
      const u = await authLogin({ email, password, turnstile_token: token });
      setUser(u);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      if (status === 403 && /confirm your email/i.test(msg)) setNeedsVerify(true);
      widget.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try { await authResendVerification(email); toast.success("Confirmation email sent — check your inbox."); }
    catch { toast.error("Could not resend. Try again."); }
  };

  return (
    <AuthLayout>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Welcome back</h1>
      <p className="text-sm text-zinc-500 mb-8">Log in to your workspace.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className={label}>Email</label>
          <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={field} placeholder="you@company.co.uk" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={label + " mb-0"}>Password</label>
            <Link to="/forgot-password" data-testid="forgot-link" className="text-[11px] font-bold text-[#2F6DA3] hover:underline">Forgot password?</Link>
          </div>
          <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={field} placeholder="••••••••" />
        </div>
        {error && <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
        {needsVerify && (
          <button type="button" onClick={resend} data-testid="resend-verification" className="flex items-center gap-2 text-xs font-bold text-[#2F6DA3] hover:underline">
            <Mail size={13} /> Resend confirmation email
          </button>
        )}
        <Turnstile ref={widget} action="login" onVerify={onVerify} />
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider py-6">
          {loading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : null} Log in
        </Button>
      </form>
      <p className="text-sm text-zinc-500 mt-6 text-center">
        No account yet? Access is <Link to="/register" data-testid="to-register" className="font-bold text-[#14304F] hover:underline">by invitation only</Link>
      </p>
    </AuthLayout>
  );
}
